import * as semver from "semver";

import {
	PrivateGatewayView,
	PrivateGateway,
	isProvisionedPrivateGatewayView
} from "@impulse/sdp-shared-models";

import { GatewayStatus } from "./models";

export class GatewayView {
	private readonly gateway: PrivateGatewayView;
	private _statuses: GatewayStatus[];

	constructor(gateway: PrivateGatewayView) {
		this.gateway = gateway;
		this._statuses = [];
		// make sure we initialize the hosts array to always have two entries, filling with empty string if necessary
		// this will allow the UI components to always get a string to back their components with
		const dnsHosts = this.gateway.dnsConfig.hosts = this.gateway.dnsConfig.hosts || [];
		while (dnsHosts.length < 2) {
			dnsHosts.push("");
		}
	}

	public get gatewayVersion() {
		if (isProvisionedPrivateGatewayView(this.gateway)) {
			let result: semver.SemVer | undefined = undefined;
			if (!!this.gateway.gatewayVersion) {
				result = semver.coerce(this.gateway.gatewayVersion) || undefined;
			}
			return result;
		} else {
			return undefined;
		}
	}

	public get label() {
		return this.gateway.label;
	}

	public set label(value: PrivateGateway.Label) {
		this.gateway.label = value;
	}

	public get type() {
		return this.gateway.type;
	}

	// setter for type intentionally omitted, must always be constant Gateway.PrivateType

	public get hostAndPorts() {
		return this.gateway.hostAndPorts;
	}

	public set hostAndPorts(value: PrivateGateway.HostAndPorts[]) {
		this.gateway.hostAndPorts = value;
	}

	public get uuid() {
		return this.gateway.uuid;
	}

	public set uuid(value: PrivateGateway.UUID) {
		this.gateway.uuid = value;
	}

	public get state() {
		return this.gateway.state;
	}

	public set state(value: PrivateGateway.ProvisioningState | PrivateGateway.ProvisionedState) {
		this.gateway.state = value;
	}

	// setter for version intentionally omitted, must always be constant PrivateGateway.Version

	public get registrationDate() {
		return this.gateway.registrationDate;
	}

	public set registrationDate(value: PrivateGateway.RegistrationDate) {
		this.gateway.registrationDate = value;
	}

	public get toggleStatus() {
		return this.state === "active";
	}

	public set toggleStatus(value: boolean) {
		this.state = value ? "active" : "inactive";
	}

	public get dnsEnabled() {
		return this.gateway.dnsConfig.enabled;
	}

	public set dnsEnabled(value: boolean) {
		this.gateway.dnsConfig.enabled = value;
	}

	public get dnsServer1() {
		return this.gateway.dnsConfig.hosts[0];
	}

	public set dnsServer1(value: string) {
		this.gateway.dnsConfig.hosts[0] = value;
	}

	public get dnsServer2() {
		return this.gateway.dnsConfig.hosts[1];
	}

	public set dnsServer2(value: string) {
		this.gateway.dnsConfig.hosts[1] = value;
	}

	public get dnsSuffixEnabled() {
		return this.gateway.dnsConfig.suffixEnabled;
	}

	public set dnsSuffixEnabled(value: boolean) {
		this.gateway.dnsConfig.suffixEnabled = value;
	}

	public get dnsSuffix() {
		return this.gateway.dnsConfig.suffix;
	}

	public set dnsSuffix(value: string | undefined) {
		this.gateway.dnsConfig.suffix = value;
	}

	public get lastHeartbeatTimestamp() {
		if (isProvisionedPrivateGatewayView(this.gateway) && !!this.gateway.heartbeat) {
			// TODO once no gateways older than 1.1.0 exist we can simplify this
			if (!this.gatewayVersion) {
				// should be impossible, but if not version information is available then treat it as though we've also never received a heartbeat
				return 0;
			} else if (semver.lt(this.gatewayVersion, "1.1.0")) {
				// gateway is too old to have both kinds of heartbeat data
				return this.gateway.heartbeat.controllerHttpTime || 0;
			}
			// return the timestamp that is most recent between the two sources
			// for each one they might be undefined, indicating no input from that source was ever received, so replace undefined with 0
			return Math.min(
				this.gateway.heartbeat.controllerHttpTime || 0,
				this.gateway.heartbeat.controllerWebsocketTime || 0
			);
		}
		return undefined;
	}

	/**
	 * @returns the time delta, in milliseconds, between when we last received a heartbeat and when the controller received that heartbeat
	 */
	public get clockSkew() {
		if (isProvisionedPrivateGatewayView(this.gateway)) {
			if (!!this.gateway.heartbeat) {
				// gateway has heartbeat data to check
				// tiny positive values are normal
				// large positive values mean delays in the network somewhere, or a private gateway that has a slow clock
				// negative values indicate that the gateway's clock is fast
				return this.gateway.heartbeat.skew;
			} else {
				// gateway is new enough that it should be able to detect clock skew, but doesn't have any heartbeat data
				return 0;
			}
		} else {
			// gateway is too old to support this feature, or is currently unprovisioned and therefore has no heartbeat data
			return 0;
		}
	}

	public set statuses(statuses: GatewayStatus[]) {
		this._statuses = statuses;
	}

	public get statuses() {
		return this._statuses;
	}
}
